import * as React from "react";

import { AnalyticsPageHeader } from "@editor/components/analytics/AnalyticsPageHeader";
import { useCurrentWorkspaceContext } from "@editor/contexts/WorkspaceDashboardContext";
import { isFeatureEnabled } from "@editor/infra/featureFlags";
import { routes } from "@editor/utils/router";

import AnalyticsContext from "@/features/analytics/AnalyticsContext";
import PageHeader from "@replo/design-system/components/PageHeader";
import { Tabs } from "@replo/design-system/components/tabs/Tabs";
import {
  generatePath,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

import AnnouncementMessage from "../announcements/AnnouncementMessage";
import { useAutoSetWorkspaceSettingsIfNull } from "../workspace/hooks/useAutoSetWorkspaceSettingsIfNull";

const ANALYTICS_TABS: {
  value: string;
  label: string;
}[] = [
  ...(isFeatureEnabled("analytics-v2-data-model")
    ? [{ value: "home", label: "Overview" }]
    : []),
  { value: "insights", label: "Insights" },
  { value: "overview", label: "Pages" },
];

const WorkspaceAnalyticsDashboard: React.FC = () => {
  useAutoSetWorkspaceSettingsIfNull();
  const navigate = useNavigate();

  const { workspace } = useCurrentWorkspaceContext();

  const pathName = useLocation().pathname.split("/").pop();

  const isDeepDive = pathName === "deep-dive";

  const workspaceName = workspace?.name;

  return (
    <AnalyticsContext>
      <div className="w-full px-6 flex flex-col overflow-hidden">
        {!isDeepDive && (
          <AnalyticsPageHeader
            header={
              <PageHeader
                title={
                  workspaceName ? `${workspaceName} Analytics` : "Analytics"
                }
                className="border-none"
              />
            }
          />
        )}
        <div className="flex flex-col gap-4 overflow-hidden">
          <AnnouncementMessage tags={["Insights"]} />
          <div>
            {pathName !== "deep-dive" && (
              <Tabs
                tabsOptions={ANALYTICS_TABS}
                value={pathName ?? "home"}
                onValueChange={(value) => {
                  const route = exhaustiveSwitch({ type: value })({
                    home: () => routes.analytics.home,
                    overview: () => routes.analytics.overview,
                    insights: () => routes.analytics.insights,
                  });
                  const path = generatePath(route, {
                    workspaceId: workspace?.id ?? "",
                  });
                  navigate(path);
                }}
                size="base"
                defaultValue="home"
              />
            )}
          </div>
          <Outlet />
        </div>
      </div>
    </AnalyticsContext>
  );
};

export default WorkspaceAnalyticsDashboard;
